import { compose } from '@descope-ui/common/utils';
import {
  getComponentName,
  injectStyle,
} from '@descope-ui/common/components-helpers';
import {
  createStyleMixin,
  draggableMixin,
  componentNameValidationMixin,
  createDynamicDataMixin,
} from '@descope-ui/common/components-mixins';
import { createBaseClass } from '@descope-ui/common/base-classes';
import { BadgeClass } from '@descope-ui/descope-badge/class';
import { ListClass } from '@descope-ui/descope-list/class';
import { ListItemClass } from '@descope-ui/descope-list-item/class';
import { getDeviceIcon, parseDate, sortFn } from './helpers';

export const componentName = getComponentName('trusted-devices');

const itemRenderer = (
  { id, name, lastLoginDate, deviceType, isCurrent },
  _,
  ref,
) => {
  const itemClassName = isCurrent ? 'class="current-device"' : '';

  const { iconSrc, iconSrcDark } = getDeviceIcon(deviceType);

  const loginLabel = ref.lastLoginLabel ? `${ref.lastLoginLabel} ` : '';

  const loginDate = parseDate(lastLoginDate, ref.format);

  const badge = isCurrent
    ? `<descope-badge
    bordered="true"
    size="xs"
    mode="primary"
    st-host-direction="${ref.direction}"
  >
    ${ref.currentDeviceLabel}
  </descope-badge>`
    : '';

  const removeDeviceLink =
    !ref.hideActions && !isCurrent
      ? `<descope-link
      class="remove-device"
      variant="body1"
      mode="primary"
      data-action="remove-device"
      data-device-id="${id}"
      st-host-direction="${ref.direction}"
      ellipsis="true"
    >
      ${ref.removeDeviceLabel}
    </descope-link>`
      : '';

  const template = document.createElement('template');

  template.innerHTML = `
    <descope-list-item ${itemClassName}>
      <div class="content">
        <div class="main">
          <span class="device">
            <descope-icon
              class="device-icon"
              src="${iconSrc}"
              src-dark="${iconSrcDark}">
            </descope-icon>
            <descope-text
              class="device-name"
              variant="body1"
              mode="primary">
            </descope-text>
          </span>
          <span class="panel">
            ${badge}
            ${removeDeviceLink}
          </span>
        </div>
        <div class="meta">
          <descope-text
            variant="body2"
            mode="primary"
          >
            ${loginLabel}
          </descope-text>
          <descope-text
            class="login-date"
            variant="body2"
            mode="primary"
          >
            ${loginDate}
          </descope-text>
        </div>
      </div>
    </descope-list-item>
  `;

  // we return a template instead of returning a string so we can avoid XSS on device name
  template.content.querySelector('.device-name').textContent = name;
  return template;
};

const BaseClass = createBaseClass({
  componentName,
  baseSelector: ListClass.componentName,
});

class RawTrustedDevicesClass extends BaseClass {
  constructor() {
    super();

    this.attachShadow({ mode: 'open' }).innerHTML = `
      <descope-list>
        <slot name="empty-state" slot="empty-state"></slot>
      </descope-list>
    `;

    this.appsList = this.shadowRoot.querySelector('descope-list');

    injectStyle(
      `
      :host {
        display: inline-block;
      }

      .descope-list-item {
        min-width: 0;
      }

      .current-device {
        order: -1;
      }

      .content {
        display: flex;
        flex-direction: column;
        width: 100%;
      }

      .main {
        display: flex;
        align-items: center;
        justify-content: space-between;
      }

      .panel {
        display: flex;
        flex-shrink: 0;
        max-width: 75%;
        overflow: hidden;
      }

      .device {
        display: flex;
        min-width: 0;
      }

      .device-icon {
        flex-shrink: 0;
      }

      .meta {
        display: flex;
      }

      .login-date {
        min-width: fit-content;
      }

      descope-badge {
        min-width: 0;
      }

      descope-text {
        display: flex;
        align-items: center;        
        min-width: 0;
      }
      descope-text::part(text-wrapper) {
        text-overflow: ellipsis;
        overflow: hidden;
        white-space: nowrap;
      }

      descope-link {
        overflow: hidden;
      }
      descope-link.remove-device::part(wrapper) {
        display: flex;
        width: 100%;
      }
      descope-link.remove-device {
        width: 100%;
      }
      `,
      this,
    );
  }

  init() {
    super.init?.();

    this.appsList.itemRenderer = itemRenderer;

    this.appsList.addEventListener('click', this.onRemoveClick.bind(this));
  }

  onRemoveClick(e) {
    if (this.readOnly) return;

    const target = e.target.closest('[data-device-id]');
    const deviceId = target?.getAttribute('data-device-id');

    if (deviceId) {
      this.dispatchEvent(
        new CustomEvent('remove-device-clicked', {
          bubbles: true,
          detail: { id: deviceId, action: 'remove-device' },
        }),
      );
    }
  }

  get readOnly() {
    return this.getAttribute('readonly') === 'true';
  }

  get hideActions() {
    return this.getAttribute('hide-actions') === 'true';
  }

  get format() {
    return this.getAttribute('format')?.toUpperCase() || 'MM-DD-YYYY';
  }

  get removeDeviceLabel() {
    return this.getAttribute('remove-device-label') || 'Sign out';
  }

  get currentDeviceLabel() {
    return this.getAttribute('current-device-label') || 'Current device';
  }

  get lastLoginLabel() {
    return this.getAttribute('last-login-label') || 'Last login:';
  }

  get direction() {
    return this.getAttribute('st-host-direction');
  }
}

const { host } = {
  host: { selector: () => ':host' },
};

export const TrustedDevicesClass = compose(
  createStyleMixin({
    mappings: {
      hostWidth: { ...host, property: 'width' },
      hostMinWidth: { ...host, property: 'min-width' },
      hostDirection: [
        { ...host, property: 'direction' },
        {
          selector: () => 'descope-list-item',
          property: 'direction',
        },
        {
          selector: () => 'descope-text',
          property: 'direction',
        },
      ],

      listItemsGap: {
        property: ListClass.cssVarList.gap,
      },
      listBackgroundColor: {
        selector: () => ListClass.componentName,
        property: ListClass.cssVarList.backgroundColor,
      },
      listBorderRadius: {
        selector: () => ListClass.componentName,
        property: ListClass.cssVarList.borderRadius,
      },
      listBorderWidth: {
        selector: () => ListClass.componentName,
        property: ListClass.cssVarList.borderWidth,
      },
      listBoxShadow: {
        selector: () => ListClass.componentName,
        property: ListClass.cssVarList.boxShadow,
      },
      listPadding: [
        {
          selector: () => ListClass.componentName,
          property: ListClass.cssVarList.verticalPadding,
        },
        {
          selector: () => ListClass.componentName,
          property: ListClass.cssVarList.horizontalPadding,
        },
      ],

      itemVerticalPadding: {
        selector: ListItemClass.componentName,
        property: ListItemClass.cssVarList.verticalPadding,
      },
      itemHorizontalPadding: {
        selector: ListItemClass.componentName,
        property: ListItemClass.cssVarList.horizontalPadding,
      },
      itemCursor: {
        selector: ListItemClass.componentName,
        property: ListItemClass.cssVarList.cursor,
      },
      itemOutline: {
        selector: ListItemClass.componentName,
        property: ListItemClass.cssVarList.outline,
      },
      itemBorderColor: {
        selector: ListItemClass.componentName,
        property: ListItemClass.cssVarList.borderColor,
      },
      itemBorderRadius: {
        selector: ListItemClass.componentName,
        property: ListItemClass.cssVarList.borderRadius,
      },
      itemBackgroundColor: {
        selector: ListItemClass.componentName,
        property: ListItemClass.cssVarList.backgroundColor,
      },
      itemContentGap: {
        selector: () => '.content',
        property: 'gap',
      },

      badgeBorderColor: {
        selector: BadgeClass.componentName,
        property: BadgeClass.cssVarList.borderColor,
      },
      badgeTextColor: {
        selector: BadgeClass.componentName,
        property: BadgeClass.cssVarList.textColor,
      },
      badgeBackgroundColor: {
        selector: BadgeClass.componentName,
        property: BadgeClass.cssVarList.backgroundColor,
      },
      badgeBorderRadius: {
        selector: BadgeClass.componentName,
        property: BadgeClass.cssVarList.borderRadius,
      },

      devicePanelGap: {
        selector: () => '.main',
        property: 'gap',
      },
      deviceIconGap: {
        selector: () => '.device',
        property: 'gap',
      },
      deviceIconSize: [
        {
          selector: () => '.device-icon',
          property: 'width',
        },
        {
          selector: () => '.device-icon',
          property: 'height',
        },
      ],

      lastLoginLabelGap: {
        selector: ' .meta',
        property: 'gap',
      },
    },
  }),
  draggableMixin,
  createDynamicDataMixin({
    itemRenderer,
    sortFn,
    rerenderAttrsList: [
      'remove-device-label',
      'current-device-label',
      'last-login-label',
      'format',
      'hide-actions',
    ],
  }),
  componentNameValidationMixin,
)(RawTrustedDevicesClass);
